import { type IDetectedBarcode, type IScannerError, type IScannerHandle, Scanner } from "@yudiel/react-qr-scanner"
import type { BarcodeFormat } from "barcode-detector/ponyfill"
import clsx from "clsx"
import { PointerEvent as ReactPointerEvent, useCallback, useEffect, useMemo, useRef, useState } from "react"

import { faIcon } from "@utils/faIcon"

import { IconButton, Tooltip } from "@atoms"

import { EmptyState, Select } from "@molecules"
import type { SelectListItem } from "@molecules/Select/SelectListItem/SelectListItem.types"

import styles from "./CodeScanner.module.scss"
import type {
    CameraCapabilities,
    CameraConstraintSet,
    CodeScannerErrorKind,
    CodeScannerProps,
} from "./CodeScanner.types"
import useCodeScannerSession from "./useCodeScannerSession"

// The codes found on part packaging and stickers, from retail UPC/EAN to the small QR variants
const CODE_FORMATS: BarcodeFormat[] = [
    "qr_code",
    "micro_qr_code",
    "rm_qr_code",
    "data_matrix",
    "aztec",
    "pdf417",
    "upc_a",
    "upc_e",
    "ean_13",
    "ean_8",
    "code_128",
    "code_39",
    "code_93",
    "codabar",
    "itf",
]

const DEFAULT_CONSTRAINTS: MediaTrackConstraints = {
    facingMode: "environment",
    width: { ideal: 1280 },
    height: { ideal: 720 },
}

// How long the camera gets to hand over its track after the video starts playing
const TRACK_POLL_INTERVAL_MS = 200
const TRACK_POLL_TIMEOUT_MS = 8000
const FLASH_MS = 350

const ERROR_COPY: Record<CodeScannerErrorKind, { title: string; supportText: string }> = {
    "permission-denied": {
        title: "Camera access is blocked",
        supportText: "Allow the camera for this site in your browser settings, then try again.",
    },
    "no-camera": {
        title: "No camera found",
        supportText: "This device has no camera Roopairs can use. Type the code instead.",
    },
    "in-use": {
        title: "The camera is busy",
        supportText: "Another app or tab is using the camera. Close it, then try again.",
    },
    "insecure-context": {
        title: "The camera needs a secure connection",
        supportText: "Open Roopairs over HTTPS to scan.",
    },
    unsupported: {
        title: "This browser can't scan",
        supportText: "Type the code instead, or open Roopairs in a current version of Safari or Chrome.",
    },
    "start-timeout": {
        title: "The camera didn't start",
        supportText: "Try again, or pick another camera.",
    },
    interrupted: {
        title: "The camera stopped",
        supportText: "Another app may have taken it over. Try again.",
    },
    "decoder-unavailable": {
        title: "The scanner couldn't load",
        supportText: "Check your connection, then try again.",
    },
    unknown: {
        title: "The camera isn't working",
        supportText: "Try again.",
    },
}

interface Camera {
    deviceId: string
    label: string
    isBackFacing: boolean
}

// Android names its cameras "camera 2, facing back"; iPhones and desktops name theirs already
function cameraFrom(device: MediaDeviceInfo): Camera {
    const androidLabel = /^camera (\d+), facing (back|front)$/i.exec(device.label)
    const label = androidLabel
        ? `${androidLabel[2].toLowerCase() === "back" ? "Back" : "Front"} camera ${androidLabel[1]}`
        : device.label

    return { deviceId: device.deviceId, label, isBackFacing: /back|rear|environment/i.test(device.label) }
}

// iPhones list each lens plus virtual cameras that switch lenses on their own; a virtual one keeps a small code
// held close in focus, so it beats whatever the browser opened first. Other devices keep the browser's choice.
function preferredBackCamera(cameras: Camera[]): Camera | undefined {
    const patterns = [/triple/i, /dual wide/i, /dual/i]
    for (const pattern of patterns) {
        const match = cameras.find((camera) => camera.isBackFacing && pattern.test(camera.label))
        if (match) {
            return match
        }
    }

    return undefined
}

function applyCameraConstraints(track: MediaStreamTrack, constraints: CameraConstraintSet): Promise<void> {
    return track.applyConstraints({ advanced: [constraints] } as MediaTrackConstraints).catch(() => {})
}

function CodeScanner(props: CodeScannerProps) {
    const { onCode, isPaused = false, className } = props
    const session = useCodeScannerSession({ isPaused })
    const { reportInterruption } = session
    const scannerRef = useRef<IScannerHandle>(null)

    const [track, setTrack] = useState<MediaStreamTrack | null>(null)
    const [capabilities, setCapabilities] = useState<CameraCapabilities>({})
    const [cameras, setCameras] = useState<Camera[]>([])
    const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null)
    const [isTorchOn, setIsTorchOn] = useState(false)
    const [isFlashing, setIsFlashing] = useState(false)
    const hasChosenDefaultCameraRef = useRef(false)
    // The camera the live track came from, so the picker shows the browser's choice before any switch
    const activeDeviceIdRef = useRef<string | null>(null)

    const pointersRef = useRef(new Map<number, { x: number; y: number }>())
    const pinchStartRef = useRef<{ distance: number; zoom: number } | null>(null)
    const zoomRef = useRef(1)

    const constraints = useMemo<MediaTrackConstraints>(
        () => (selectedDeviceId ? { deviceId: { exact: selectedDeviceId } } : DEFAULT_CONSTRAINTS),
        [selectedDeviceId],
    )
    const isScanning = session.status === "scanning"

    // The library hands the track over only through its ref, some time after the video starts, so poll for it once
    // a session is live; the track is where the controls, the camera list, and the "camera stopped" events live
    useEffect(() => {
        if (!isScanning) {
            setTrack(null)
            return
        }

        let isCurrent = true
        let elapsed = 0
        const timer = window.setInterval(() => {
            const liveTrack = scannerRef.current?.getStream()?.getVideoTracks()[0]
            elapsed += TRACK_POLL_INTERVAL_MS
            if (liveTrack && liveTrack.readyState === "live") {
                window.clearInterval(timer)
                if (isCurrent) {
                    setTrack(liveTrack)
                }
            } else if (elapsed >= TRACK_POLL_TIMEOUT_MS) {
                window.clearInterval(timer)
            }
        }, TRACK_POLL_INTERVAL_MS)

        return () => {
            isCurrent = false
            window.clearInterval(timer)
        }
    }, [isScanning, session.sessionId, selectedDeviceId])

    useEffect(() => {
        if (!track) {
            setCapabilities({})
            setIsTorchOn(false)
            zoomRef.current = 1
            return
        }

        setCapabilities((track.getCapabilities?.() as CameraCapabilities | undefined) ?? {})
        zoomRef.current = (track.getSettings() as { zoom?: number }).zoom ?? 1
        activeDeviceIdRef.current = track.getSettings().deviceId ?? null

        track.addEventListener("ended", reportInterruption)
        track.addEventListener("mute", reportInterruption)

        // Labels only exist once the camera is granted, which it is by the time a track is live
        void navigator.mediaDevices
            .enumerateDevices()
            .then((devices) => {
                const videoCameras = devices
                    .filter((device) => device.kind === "videoinput" && device.label !== "")
                    .map(cameraFrom)
                    .sort((cameraA, cameraB) =>
                        cameraA.label.localeCompare(cameraB.label, undefined, { numeric: true }),
                    )
                setCameras(videoCameras)

                if (!hasChosenDefaultCameraRef.current) {
                    hasChosenDefaultCameraRef.current = true
                    const preferred = preferredBackCamera(videoCameras)
                    if (preferred && preferred.deviceId !== track.getSettings().deviceId) {
                        setSelectedDeviceId(preferred.deviceId)
                    }
                }
            })
            .catch(() => {})

        return () => {
            track.removeEventListener("ended", reportInterruption)
            track.removeEventListener("mute", reportInterruption)
        }
    }, [track, reportInterruption])

    const { acceptCodes, reportError, retry, status } = session

    const handleScan = useCallback(
        (codes: IDetectedBarcode[]) => {
            const code = acceptCodes(codes, scannerRef.current?.getVideoElement() ?? null)
            if (code) {
                navigator.vibrate?.(60)
                setIsFlashing(true)
                window.setTimeout(() => setIsFlashing(false), FLASH_MS)
                onCode(code)
            }
        },
        [acceptCodes, onCode],
    )

    const handleError = useCallback(
        (error: IScannerError) => {
            // The library leaves the stream open when the video never starts playing
            const orphanedStream = scannerRef.current?.getVideoElement()?.srcObject
            if (orphanedStream instanceof MediaStream) {
                orphanedStream.getTracks().forEach((orphanedTrack) => orphanedTrack.stop())
            }

            reportError(error)
        },
        [reportError],
    )

    const handleToggleTorch = useCallback(() => {
        if (track) {
            const nextTorch = !isTorchOn
            setIsTorchOn(nextTorch)
            void applyCameraConstraints(track, { torch: nextTorch })
        }
    }, [track, isTorchOn])

    const handleSelectCamera = useCallback(
        (item: SelectListItem) => {
            setSelectedDeviceId(item.value)
            // A camera picked while the last one failed to open starts a fresh session on it
            if (status === "error") {
                retry()
            }
        },
        [status, retry],
    )

    const applyZoom = useCallback(
        (zoom: number) => {
            if (track && capabilities.zoom) {
                const clamped = Math.min(capabilities.zoom.max, Math.max(capabilities.zoom.min, zoom))
                zoomRef.current = clamped
                // Phones can't hold the torch through a zoom change, so it goes off first
                if (isTorchOn) {
                    setIsTorchOn(false)
                    void applyCameraConstraints(track, { torch: false, zoom: clamped })
                } else {
                    void applyCameraConstraints(track, { zoom: clamped })
                }
            }
        },
        [track, capabilities.zoom, isTorchOn],
    )

    function pinchDistance(): number {
        const [first, second] = Array.from(pointersRef.current.values())
        return Math.hypot(first.x - second.x, first.y - second.y)
    }

    function handlePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
        pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
        event.currentTarget.setPointerCapture(event.pointerId)
        if (pointersRef.current.size === 2) {
            pinchStartRef.current = { distance: pinchDistance(), zoom: zoomRef.current }
        }
    }

    function handlePointerMove(event: ReactPointerEvent<HTMLDivElement>) {
        if (!pointersRef.current.has(event.pointerId)) {
            return
        }
        pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
        const pinchStart = pinchStartRef.current
        if (pointersRef.current.size === 2 && pinchStart) {
            applyZoom(pinchStart.zoom * (pinchDistance() / pinchStart.distance))
        }
    }

    function handlePointerUp(event: ReactPointerEvent<HTMLDivElement>) {
        pointersRef.current.delete(event.pointerId)
        if (pointersRef.current.size < 2) {
            pinchStartRef.current = null
        }
    }

    const activeDeviceId = selectedDeviceId ?? activeDeviceIdRef.current
    const selectedCamera = cameras.find((camera) => camera.deviceId === activeDeviceId)
    const cameraItems: SelectListItem[] = cameras.map((camera) => ({ label: camera.label, value: camera.deviceId }))
    const isCameraListKnown = cameras.length > 0

    const controls = (
        <div className={styles.controls}>
            <div className={styles.cameraPicker}>
                <Select.Root>
                    <Select.Input
                        isControlled={false}
                        size="sm"
                        label="Camera"
                        placeholder={isCameraListKnown ? "Camera" : "Finding cameras…"}
                        isDisabled={cameras.length < 2}
                        selectedItem={
                            selectedCamera
                                ? { label: selectedCamera.label, value: selectedCamera.deviceId }
                                : undefined
                        }
                    />
                    <Select.Viewport>
                        <Select.List title="Camera">
                            <Select.ListGroup>
                                {cameraItems.map((item) => (
                                    <Select.ListItem
                                        key={item.value}
                                        variant="default"
                                        item={item}
                                        isSelected={item.value === selectedCamera?.deviceId}
                                        onSelect={handleSelectCamera}
                                    />
                                ))}
                            </Select.ListGroup>
                        </Select.List>
                    </Select.Viewport>
                </Select.Root>
            </div>
            {capabilities.torch && (
                <Tooltip body={isTorchOn ? "Turn the torch off" : "Turn the torch on"}>
                    <IconButton
                        size="lg"
                        variant={isTorchOn ? "solid" : "subtle"}
                        icon={faIcon({ name: "bolt", style: "regular" })}
                        iconOnActive={faIcon({ name: "bolt-slash", style: "regular" })}
                        iconIsActive={isTorchOn}
                        aria-label={isTorchOn ? "Turn the torch off" : "Turn the torch on"}
                        aria-pressed={isTorchOn}
                        onClick={handleToggleTorch}
                        noDebounce={true}
                    />
                </Tooltip>
            )}
        </div>
    )

    if (session.status === "error" && session.error) {
        const copy = ERROR_COPY[session.error]
        return (
            <div className={clsx(styles.scanner, className)}>
                <EmptyState
                    variant="error"
                    icon={faIcon({ name: "camera-slash", style: "regular" })}
                    title={copy.title}
                    supportText={copy.supportText}
                    primaryCTA={session.canRetry ? { children: "Try again", onClick: retry } : undefined}
                />
                {isCameraListKnown && controls}
            </div>
        )
    }

    return (
        <div className={clsx(styles.scanner, className)}>
            <div
                className={styles.viewfinder}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
            >
                {session.status !== "preparing" && (
                    <Scanner
                        key={session.sessionId}
                        ref={scannerRef}
                        onScan={handleScan}
                        onError={handleError}
                        constraints={constraints}
                        formats={CODE_FORMATS}
                        paused={!isScanning}
                        allowMultiple={true}
                        scanDelay={300}
                        retryDelay={100}
                        sound={false}
                        components={{ finder: false, onOff: false, torch: false, zoom: false }}
                        styles={{ container: { aspectRatio: "auto" } }}
                    />
                )}
                <div className={styles.overlay} aria-hidden="true">
                    <span className={clsx(styles.corner, styles.topLeft)} />
                    <span className={clsx(styles.corner, styles.topRight)} />
                    <span className={clsx(styles.corner, styles.bottomLeft)} />
                    <span className={clsx(styles.corner, styles.bottomRight)} />
                    <span className={clsx(styles.flash, isFlashing && styles.isFlashing)} />
                    {session.status === "preparing" ? (
                        <span className={styles.preparing}>Preparing the scanner…</span>
                    ) : (
                        <span className={styles.hint}>Point the camera at a QR code or barcode</span>
                    )}
                </div>
            </div>
            {controls}
        </div>
    )
}

export default CodeScanner
